import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  LinearProgress,
  Divider,
  Alert,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Inventory as InventoryIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Restore as RestoreIcon,
  ShowChart as ShowChartIcon,
  LocalShipping as ShippingIcon,
  ShoppingCart as CartIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { toast } from 'react-toastify';
import {
  getInventoryItemDetails,
  getProductTransactionHistory,
  adjustInventory,
  markProductAsObsolete,
  markProductAsActive,
} from '../services/inventoryService';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const ProductInventoryDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [productDetails, setProductDetails] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);
  
  // Adjustment Dialog State
  const [adjustmentDialog, setAdjustmentDialog] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: '',
    reason: '',
    referenceNumber: '',
    type: 'increase', // 'increase' or 'decrease'
  });

  // Fetch product details
  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const response = await getInventoryItemDetails(productId);
      if (response?.payload) {
        setProductDetails(response.payload);
      }
    } catch (error) {
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch transaction history
  const fetchTransactions = async () => {
    try {
      const response = await getProductTransactionHistory(
        productId,
        page + 1,
        rowsPerPage
      );
      if (response?.payload?.Items) {
        setTransactions(response.payload.Items);
        setTotalTransactions(response.payload.TotalItems || 0);
      }
    } catch (error) {
      toast.error('Failed to load transactions');
    }
  };

  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchTransactions();
    }
  }, [activeTab, page, rowsPerPage, productId]);

  // Handle stock adjustment
  const handleAdjustStock = async () => {
    try {
      const quantityChange = adjustmentData.type === 'increase' 
        ? Math.abs(parseInt(adjustmentData.quantity))
        : -Math.abs(parseInt(adjustmentData.quantity));

      const payload = {
        ProductId: parseInt(productId),
        QuantityChange: quantityChange,
        Reason: adjustmentData.reason,
        ReferenceNumber: adjustmentData.referenceNumber || undefined,
      };

      await adjustInventory(payload);
      toast.success('Stock adjusted successfully');
      setAdjustmentDialog(false);
      setAdjustmentData({
        quantity: '',
        reason: '',
        referenceNumber: '',
        type: 'increase',
      });
      fetchProductDetails();
      if (activeTab === 1) {
        fetchTransactions();
      }
    } catch (error) {
      toast.error('Failed to adjust stock');
    }
  };

  // Handle obsolete/unobsolete
  const handleToggleObsolete = async () => {
    try {
      if (productDetails?.IsObsolete) {
        await markProductAsActive(productId);
        toast.success('Product marked as active');
      } else {
        await markProductAsObsolete(productId);
        toast.success('Product marked as obsolete');
      }
      fetchProductDetails();
    } catch (error) {
      toast.error('Failed to update product status');
    }
  };

  // Chart data
  const getChartData = () => {
    if (!transactions || transactions.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Sort transactions by date
    const sortedTransactions = [...transactions].sort(
      (a, b) => new Date(a.TransactionDate) - new Date(b.TransactionDate)
    );

    // Calculate cumulative stock levels
    let currentStock = productDetails?.CurrentStock || 0;
    const stockLevels = [];
    const labels = [];

    // Work backwards from current stock
    for (let i = sortedTransactions.length - 1; i >= 0; i--) {
      const transaction = sortedTransactions[i];
      labels.unshift(formatDate(transaction.TransactionDate));
      stockLevels.unshift(currentStock);
      currentStock -= transaction.QuantityChange;
    }

    // Add current stock point
    labels.push('Current');
    stockLevels.push(productDetails?.CurrentStock || 0);

    return {
      labels,
      datasets: [
        {
          label: 'Stock Level',
          data: stockLevels,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Min Stock',
          data: Array(labels.length).fill(productDetails?.MinStock || 0),
          borderColor: 'rgb(255, 99, 132)',
          borderDash: [5, 5],
          fill: false,
        },
        {
          label: 'Max Stock',
          data: Array(labels.length).fill(productDetails?.MaxStock || 0),
          borderColor: 'rgb(54, 162, 235)',
          borderDash: [5, 5],
          fill: false,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Stock Level History',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStockStatus = () => {
    if (!productDetails) return { text: 'Unknown', color: 'default' };
    if (productDetails.IsObsolete) return { text: 'Obsolete', color: 'error' };
    if (productDetails.CurrentStock === 0) return { text: 'Out of Stock', color: 'error' };
    if (productDetails.CurrentStock <= productDetails.MinStock) return { text: 'Low Stock', color: 'warning' };
    if (productDetails.MaxStock && productDetails.CurrentStock >= productDetails.MaxStock) 
      return { text: 'Overstock', color: 'info' };
    return { text: 'In Stock', color: 'success' };
  };

  const getTransactionIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'purchase':
      case 'adjustment_increase':
        return <AddCircleIcon fontSize="small" color="success" />;
      case 'sale':
      case 'adjustment_decrease':
        return <RemoveCircleIcon fontSize="small" color="error" />;
      case 'return':
        return <RestoreIcon fontSize="small" color="info" />;
      default:
        return <HistoryIcon fontSize="small" />;
    }
  };

  const calculateStockPercentage = () => {
    if (!productDetails?.MaxStock) return 0;
    return (productDetails.CurrentStock / productDetails.MaxStock) * 100;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading product details...</Typography>
      </Box>
    );
  }

  if (!productDetails) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Product not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/inventory')} sx={{ mt: 2 }}>
          Back to Inventory
        </Button>
      </Box>
    );
  }

  const stockStatus = getStockStatus();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/inventory')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {productDetails.ProductName}
        </Typography>
        <Chip
          label={stockStatus.text}
          color={stockStatus.color}
          icon={stockStatus.color === 'success' ? <CheckCircleIcon /> : <WarningIcon />}
          sx={{ mr: 2 }}
        />
        {productDetails.IsObsolete ? (
          <Button
            variant="contained"
            color="success"
            startIcon={<RestoreIcon />}
            onClick={handleToggleObsolete}
            sx={{ mr: 2 }}
          >
            Mark as Active
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="error"
            startIcon={<BlockIcon />}
            onClick={handleToggleObsolete}
            sx={{ mr: 2 }}
          >
            Mark as Obsolete
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setAdjustmentDialog(true)}
        >
          Adjust Stock
        </Button>
      </Box>

      {/* Product Info Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InventoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Current Stock
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {productDetails.CurrentStock}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                SKU: {productDetails.SKU || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Min / Max Stock
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {productDetails.MinStock}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  / {productDetails.MaxStock || 'N/A'}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={calculateStockPercentage()}
                color={stockStatus.color}
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Cost / Sale Price
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  €{productDetails.CostPrice?.toFixed(2)}
                </Typography>
                <Typography variant="h6" color="success.main">
                  / €{productDetails.SalePrice?.toFixed(2)}
                </Typography>
              </Box>
              <Typography variant="caption" color="success.main">
                Margin: €{((productDetails.SalePrice || 0) - (productDetails.CostPrice || 0)).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Inventory Value
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                €{((productDetails.CurrentStock || 0) * (productDetails.CostPrice || 0)).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {productDetails.CurrentStock} units × €{productDetails.CostPrice?.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Info */}
      {productDetails.SupplierName && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Supplier</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                <ShippingIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                {productDetails.SupplierName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Last Updated</Typography>
              <Typography variant="body1">{formatDateTime(productDetails.LastUpdated)}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Category</Typography>
              <Typography variant="body1">{productDetails.Category || 'Uncategorized'}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<ShowChartIcon />} label="Stock Chart" iconPosition="start" />
          <Tab icon={<HistoryIcon />} label="Transaction History" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3, height: 450 }}>
          <Line data={getChartData()} options={chartOptions} />
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Quantity Change</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Stock After</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No transactions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.Id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(transaction.TransactionDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getTransactionIcon(transaction.TransactionType)}
                          <Typography variant="body2">
                            {transaction.TransactionType?.replace(/_/g, ' ')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={transaction.QuantityChange > 0 
                            ? `+${transaction.QuantityChange}` 
                            : transaction.QuantityChange}
                          size="small"
                          color={transaction.QuantityChange > 0 ? 'success' : 'error'}
                          icon={transaction.QuantityChange > 0 
                            ? <TrendingUpIcon /> 
                            : <TrendingDownIcon />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {transaction.StockAfterTransaction}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {transaction.Reason || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {transaction.ReferenceNumber || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {transaction.UserName || 'System'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalTransactions}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Adjustment Dialog */}
      <Dialog open={adjustmentDialog} onClose={() => setAdjustmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Stock - {productDetails.ProductName}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              select
              fullWidth
              label="Adjustment Type"
              value={adjustmentData.type}
              onChange={(e) => setAdjustmentData({ ...adjustmentData, type: e.target.value })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="increase">Increase Stock</MenuItem>
              <MenuItem value="decrease">Decrease Stock</MenuItem>
            </TextField>

            <TextField
              fullWidth
              type="number"
              label="Quantity"
              value={adjustmentData.quantity}
              onChange={(e) => setAdjustmentData({ ...adjustmentData, quantity: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ min: 1 }}
              helperText={`Current stock: ${productDetails.CurrentStock}`}
            />

            <TextField
              fullWidth
              label="Reason"
              value={adjustmentData.reason}
              onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Reference Number (Optional)"
              value={adjustmentData.referenceNumber}
              onChange={(e) => setAdjustmentData({ ...adjustmentData, referenceNumber: e.target.value })}
              placeholder="e.g., PO-12345, INV-67890"
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              {adjustmentData.type === 'increase' ? (
                <>New stock will be: <strong>{productDetails.CurrentStock + parseInt(adjustmentData.quantity || 0)}</strong></>
              ) : (
                <>New stock will be: <strong>{productDetails.CurrentStock - parseInt(adjustmentData.quantity || 0)}</strong></>
              )}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustmentDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdjustStock}
            disabled={!adjustmentData.quantity || !adjustmentData.reason}
          >
            Confirm Adjustment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductInventoryDetail;
